import { useLayoutEffect, useRef, useState } from "react";
import styles from "./index.module.css";

const SHOW_MORE_TEXT = "більше";

/**
 * Fills the space its card leaves, cuts the content off there (never lower than
 * --clamp-height) and, only when something was actually cut, fades the bottom
 * edge and offers a "більше" button.
 *
 * Cards in a slider are stretched to a common height, so the cut-off point is
 * the card's, not a fixed number of lines — a short text in a tall card is shown
 * whole. Put --clamp-height (and, on anything that is not white, --clamp-fade /
 * --clamp-fade-start) on the card, so the cap can follow media queries.
 */
export default function Clampable({
  children,
  className,
  expanded = false,
  onShowMore,
}) {
  const boxRef = useRef(null);
  const contentRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const content = contentRef.current;

    if (!box || !content) return;

    const checkOverflow = () =>
      setIsTruncated(!expanded && box.scrollHeight > box.clientHeight + 1);

    checkOverflow();

    let resizeObserver;

    if ("ResizeObserver" in window) {
      // the box stops at the card's edge, so watch what grows inside it
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(content);
    }

    window.addEventListener("resize", checkOverflow);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [expanded]);

  return (
    <>
      <div
        ref={boxRef}
        className={[styles.clampWrap, expanded && styles.expandedWrap]
          .filter(Boolean)
          .join(" ")}
      >
        <div ref={contentRef} className={className}>
          {children}
        </div>
        {isTruncated && <span className={styles.fade} />}
      </div>

      {isTruncated && onShowMore && (
        <button type="button" className={styles.showMore} onClick={onShowMore}>
          {SHOW_MORE_TEXT}
        </button>
      )}
    </>
  );
}
